import React from "react";
import {
  Container,
  Box,
  Typography,
  Button,
  TextField,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Select,
  Checkbox,
  Card,
  CardContent,
  InputLabel,
  Paper,
} from "@mui/material";

const ImageCompressor = () => {
  const [originalImage, setOriginalImage] = React.useState(null);
  const [resizedImage, setResizedImage] = React.useState(null);
  const [fileName, setFileName] = React.useState("");
  const [resizeOption, setResizeOption] = React.useState("");
  const [customWidth, setCustomWidth] = React.useState("");
  const [customHeight, setCustomHeight] = React.useState("");
  const [lockAspectRatio, setLockAspectRatio] = React.useState(true);
  const [originalWidth, setOriginalWidth] = React.useState(null);
  const [originalHeight, setOriginalHeight] = React.useState(null);
  const [resizeMode, setResizeMode] = React.useState("fileSize"); // "fileSize" or "dimensions"

  // Drag & Drop states
  const [isDragging, setIsDragging] = React.useState(false);

  const sizeOptions = [
    { label: "Under 5MB", bytes: 5 * 1024 * 1024 },
    { label: "Under 1MB", bytes: 1 * 1024 * 1024 },
    { label: "Under 500KB", bytes: 500 * 1024 },
    { label: "Under 200KB", bytes: 200 * 1024 },
    { label: "Under 100KB", bytes: 100 * 1024 },
  ];

  const handleFileChange = async (file) => {
    if (!file) return;
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (loadEvent) => {
      setOriginalImage(loadEvent.target.result);
      setResizedImage(null);
      // Pre-load image to extract dimensions
      const img = new Image();
      img.onload = () => {
        setOriginalWidth(img.width);
        setOriginalHeight(img.height);
        setCustomWidth(img.width);
        setCustomHeight(img.height);
      };
      img.src = loadEvent.target.result;
    };
    reader.readAsDataURL(file);
  };

  // Input onChange
  const onInputChange = (e) => {
    const file = e.target.files[0];
    handleFileChange(file);
  };

  // Drag & Drop Handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange(e.dataTransfer.files[0]);
    }
  };

  const handleResizeOptionChange = (e) => {
    setResizeOption(e.target.value);
  };

  const handleWidthChange = (e) => {
    const value = e.target.value;
    setCustomWidth(value);

    // If aspect ratio is locked, update height
    if (lockAspectRatio && originalWidth && originalHeight) {
      const newHeight = Math.round((originalHeight / originalWidth) * value);
      setCustomHeight(newHeight);
    }
  };

  const handleHeightChange = (e) => {
    const value = e.target.value;
    setCustomHeight(value);

    // If aspect ratio is locked, update width
    if (lockAspectRatio && originalWidth && originalHeight) {
      const newWidth = Math.round((originalWidth / originalHeight) * value);
      setCustomWidth(newWidth);
    }
  };

  const resizeByFileSize = async (canvas) => {
    if (!resizeOption || !originalImage) return;
    const maxBytes = parseInt(resizeOption, 10);

    let quality = 1.0;
    let resizedDataUrl = canvas.toDataURL("image/jpeg", quality);
    let currentSize = Math.round((resizedDataUrl.length * 3) / 4);

    // Iteratively reduce quality
    while (currentSize > maxBytes && quality > 0) {
      quality -= 0.05;
      if (quality < 0.05) quality = 0.05; // minimal quality threshold
      resizedDataUrl = canvas.toDataURL("image/jpeg", quality);
      currentSize = Math.round((resizedDataUrl.length * 3) / 4);
    }
    setResizedImage(resizedDataUrl);
  };

  const resizeByDimensions = (image) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = parseInt(customWidth, 10) || image.width;
    canvas.height = parseInt(customHeight, 10) || image.height;

    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    // Default quality to 0.9
    const resizedDataUrl = canvas.toDataURL("image/jpeg", 0.9);
    setResizedImage(resizedDataUrl);
  };

  const handleResize = async () => {
    if (!originalImage) return;
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      if (resizeMode === "fileSize") {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0, img.width, img.height);
        resizeByFileSize(canvas);
      } else if (resizeMode === "dimensions") {
        resizeByDimensions(img);
      }
    };
    img.src = originalImage;
  };

  const handleReset = () => {
    setResizedImage(null);
    setCustomWidth(originalWidth);
    setCustomHeight(originalHeight);
  };

  const handleDownload = () => {
    if (!resizedImage) return;
    const link = document.createElement("a");
    link.href = resizedImage;
    link.download = fileName
      ? `compressed-${fileName}`
      : "compressed-image.jpg";
    link.click();
  };
  return (
    <Container maxWidth="md" sx={{ py: 2 }}>
      <Box
        sx={{
          mb: 3,
          py: 2,
          textAlign: "center",
          backgroundColor: "#f2f2f2",
        }}
      >
        <Typography variant="h4" gutterBottom align="center" sx={{ mb: 1 }}>
          Image Compressor
        </Typography>
      </Box>
      <Paper
        elevation={3}
        sx={{
          py: 3,
          px: 2,
          mb: 3,
        }}
      >
        {/* Header */}

        {/* Drag and Drop & File Selection */}
        <Box
          sx={{
            border: isDragging ? "2px dashed #1976d2" : "2px dashed #ccc",
            borderRadius: 2,
            p: 2,
            textAlign: "center",
            transition: "border 0.2s",
            cursor: "pointer",
            backgroundColor: isDragging ? "#f0f8ff" : "transparent",
          }}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <Typography variant="body1" sx={{ mb: 2 }}>
            {isDragging
              ? "Drop your image here"
              : "Drag & Drop image or click the button below"}
          </Typography>
          <Button variant="outlined" component="label">
            Select an Image
            <input
              type="file"
              accept="image/*"
              onChange={onInputChange}
              hidden
            />
          </Button>
          {fileName && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              Selected file: <strong>{fileName}</strong>
            </Typography>
          )}
        </Box>

        {/* Compression Options */}
        <Paper elevation={3} sx={{ py: 3, px: 2, mt: 3 }}>
          {/* Resize Mode Selection */}
          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <FormLabel component="legend">Compress Mode</FormLabel>
            <RadioGroup
              row
              value={resizeMode}
              onChange={(e) => setResizeMode(e.target.value)}
            >
              <FormControlLabel
                value="fileSize"
                control={<Radio />}
                label="By File Size"
              />
              <FormControlLabel
                value="dimensions"
                control={<Radio />}
                label="By Dimensions"
              />
            </RadioGroup>
          </FormControl>

          {/* File Size Mode */}
          {resizeMode === "fileSize" && (
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth>
                <InputLabel id="file-size-select-label">
                  Choose Target Size
                </InputLabel>
                <Select
                  labelId="file-size-select-label"
                  label="Choose Target Size"
                  onChange={handleResizeOptionChange}
                  value={resizeOption || ""}
                >
                  <MenuItem value="">--Select--</MenuItem>
                  {sizeOptions.map((opt, index) => (
                    <MenuItem key={index} value={opt.bytes}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Dimensions Mode */}
          {resizeMode === "dimensions" && (
            <Box sx={{ mb: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                label="Width"
                type="number"
                value={customWidth}
                onChange={handleWidthChange}
                sx={{ width: "150px" }}
              />
              <TextField
                label="Height"
                type="number"
                value={customHeight}
                onChange={handleHeightChange}
                sx={{ width: "150px" }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={lockAspectRatio}
                    onChange={(e) => setLockAspectRatio(e.target.checked)}
                  />
                }
                label="Lock Aspect Ratio"
              />
              {originalWidth && originalHeight && (
                <Typography variant="body2" sx={{ alignSelf: "center" }}>
                  Original dimensions: {originalWidth} × {originalHeight}
                </Typography>
              )}
            </Box>
          )}

          {/* Action Buttons */}
          <Box sx={{ mb: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Button variant="contained" onClick={handleResize}>
              Compress
            </Button>
            <Button variant="outlined" onClick={handleReset}>
              Reset
            </Button>
            {resizedImage && (
              <Button
                variant="contained"
                color="success"
                onClick={handleDownload}
              >
                Download
              </Button>
            )}
          </Box>

          {/* Display Images */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 4,
              justifyContent: "center",
              alignItems: "flex-start",
              mt: 4,
            }}
          >
            {/* Original Image */}
            <Card sx={{ maxWidth: 400 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Original Image
                </Typography>
                {originalImage ? (
                  <Box
                    component="img"
                    src={originalImage}
                    alt="original"
                    sx={{ maxWidth: "100%", borderRadius: 2 }}
                  />
                ) : (
                  <Typography>No image selected yet.</Typography>
                )}
              </CardContent>
            </Card>

            {/* Resized Image */}
            <Card sx={{ maxWidth: 400 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Compressed Image
                </Typography>
                {resizedImage ? (
                  <>
                    <Box
                      component="img"
                      src={resizedImage}
                      alt="resized"
                      sx={{ maxWidth: "100%", borderRadius: 2 }}
                    />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Approx size:{" "}
                      <strong>
                        {Math.round((resizedImage.length * 3) / 4 / 1024)} KB
                      </strong>
                    </Typography>
                  </>
                ) : (
                  <Typography>No resized image yet.</Typography>
                )}
              </CardContent>
            </Card>
          </Box>
        </Paper>
      </Paper>
    </Container>
  );
};

export default ImageCompressor;
